import { TypographyMuted, TypographyH3 } from '@/components/ui/typography';
import useDisclosure from '@/hooks/useDisclosure';
import {
  CircleArrowLeft,
  Home,
  MessageCirclePlus,
  Package,
  Save,
  Store,
  Search as SearchIcon,
  ChevronDown,
  ChevronUp,
  Inbox,
} from 'lucide-react';
import AddProductMarketResearchDialog from '../AddProductMarketResearchDialog';
import { useOffline } from '@/context/OfflineContext';
import { MarketResearch, ResearchedProduct } from '@/types/MarketResearch';
import ProductsOfflineDialog from '../ProductsOfflineDialog';
import CardsResearchedProducts from './components/CardsResearchedProducts';
import ViewImageDialog from '../ViewImageDialog';
import { useEffect, useMemo, useState } from 'react';
import { useImageResearchedProductQuery } from '@/queries/useImageResearchedProductQuery';
import Lottie from 'lottie-react';
import Money from '@/assets/lottie/Insider-loading.json';
import EditProductMarketResearchDialog from '../EditProductMarketResearchDialog';
import DeleteProductMarketResearchDiaload from '../DeleteProductMarketResearchDiaload';
import Button from '@/components/Button';
import AddNewProductInProductMarketResearchDialog from './components/AddNewProductInProductMarketResearchDialog';
import { Link, useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';

interface props {
  marketResearch: MarketResearch;
  dataResearchedProducts: ResearchedProduct[];
}

const AgentLayout = ({ marketResearch, dataResearchedProducts }: props) => {
  const { data } = useOffline();
  const [status, setStatus] = useState(false);
  const [keyImage, setkeyImage] = useState<string>('');
  const [srcImage, setSrcImage] = useState<string>('');
  const [selectProduct, setSelectProduct] = useState<ResearchedProduct>();
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [expandedClients, setExpandedClients] = useState<
    Record<number, boolean>
  >({});
  const navigate = useNavigate();

  const toggleExpandClient = (skCliente: number) => {
    setExpandedClients((prev) => ({
      ...prev,
      [skCliente]: !prev[skCliente],
    }));
  };

  const {
    isOpen: isOpenAddProduct,
    onClose: onCloseAddProduct,
    onOpen: onOpenAddProduct,
  } = useDisclosure();

  const {
    isOpen: isOpenEditProduct,
    onClose: onCloseEditProduct,
    onOpen: onOpenEditProduct,
  } = useDisclosure();

  const {
    isOpen: isOpenDeleteProduct,
    onClose: onCloseDeleteProduct,
    onOpen: onOpenDeleteProduct,
  } = useDisclosure();

  const {
    isOpen: isOpenProductOffline,
    onClose: onCloseProductOffline,
    onOpen: onOpenProductOffline,
  } = useDisclosure();

  const {
    isOpen: isOpenViewImage,
    onClose: onCloseViewImage,
    onOpen: onOpenViewImage,
  } = useDisclosure();

  const {
    isOpen: isOpenAddNewProduct,
    onClose: onCloseAddNewProduct,
    onOpen: onOpenAddNewProduct,
  } = useDisclosure();

  const { data: dataImage, isFetching } = useImageResearchedProductQuery(
    keyImage,
    status,
  );

  const handleViewImage = (key: string) => {
    setkeyImage(key);
    setStatus(true);
  };

  const handleEditProduct = (productResearched: ResearchedProduct) => {
    setSelectProduct(productResearched);
    onOpenEditProduct();
  };

  const handleDeleteProduct = (productResearched: ResearchedProduct) => {
    setSelectProduct(productResearched);
    onOpenDeleteProduct();
  };

  useEffect(() => {
    if (dataImage) {
      setSrcImage(dataImage.url);
      onOpenViewImage();
    }
  }, [dataImage, keyImage]);

  const groupedByClient = useMemo(() => {
    if (!dataResearchedProducts || dataResearchedProducts.length === 0)
      return [];

    const map = new Map<
      number,
      {
        skCliente: number;
        nomeFantasia: string;
        dsRevenda?: string;
        products: ResearchedProduct[];
      }
    >();

    const filterLower = searchFilter.trim().toLowerCase();

    for (const item of dataResearchedProducts) {
      const matchFilter =
        !filterLower ||
        (item.DS_NOME_FANTASIA &&
          item.DS_NOME_FANTASIA.toLowerCase().includes(filterLower)) ||
        (item.DS_PRODUTO &&
          item.DS_PRODUTO.toLowerCase().includes(filterLower)) ||
        String(item.SK_CLIENTE).includes(filterLower) ||
        (item.DS_REVENDA &&
          item.DS_REVENDA.toLowerCase().includes(filterLower));

      if (!matchFilter) continue;

      const key = item.SK_CLIENTE;
      if (!map.has(key)) {
        map.set(key, {
          skCliente: key,
          nomeFantasia: item.DS_NOME_FANTASIA || `Cliente #${key}`,
          dsRevenda: item.DS_REVENDA,
          products: [],
        });
      }
      map.get(key)!.products.push(item);
    }

    return Array.from(map.values());
  }, [dataResearchedProducts, searchFilter]);

  const totalProducts = dataResearchedProducts?.length || 0;

  return (
    <div className="flex flex-col h-full overflow-auto py-4 bg-gray-50">
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-gradient-sidnav-400 md:hidden">
        <div className="grid [grid-template-columns:repeat(auto-fit,minmax(0,1fr))] items-center h-16">
          <div className="flex justify-center">
            <Link
              className="flex flex-col items-center text-xs text-white"
              to={'/'}
            >
              <Home size={18} />
              <TypographyMuted className="text-white">Inicio</TypographyMuted>
            </Link>
          </div>
          <div className="flex justify-center">
            <button
              className="flex flex-col items-center text-xs text-white"
              onClick={() => navigate(-1)}
            >
              <CircleArrowLeft size={18} />
              <TypographyMuted className="text-white">Voltar</TypographyMuted>
            </button>
          </div>
          {marketResearch.DD_STATUS == 'Publicado' && (
            <>
              <div className="flex justify-center">
                <button
                  className="flex flex-col items-center text-xs text-white"
                  onClick={() =>
                    navigate(
                      `/market-research/packaging-research?search=${marketResearch.SK_PESQUISA}`,
                    )
                  }
                >
                  <MessageCirclePlus size={18} />
                  <TypographyMuted className="text-white">
                    Informar
                  </TypographyMuted>
                </button>
              </div>
              {/* <div className="flex justify-center">
                <button
                  className="flex flex-col items-center text-xs text-white"
                  onClick={() => onOpenAddProduct()}
                >
                  <MessageCirclePlus size={18} />
                  <TypographyMuted className="text-white">
                    Informar
                  </TypographyMuted>
                </button>
              </div> */}
              {/* <div className="flex justify-center">
                <button
                  className="flex flex-col items-center text-xs text-white"
                  onClick={() => onOpenAddNewProduct()}
                >
                  <Megaphone size={18} />
                  <TypographyMuted className="text-white">Novo</TypographyMuted>
                </button>
              </div> */}
              {data.ProductsFromCustomer?.length > 0 && (
                <div className="flex justify-center">
                  <button
                    className="flex flex-col items-center text-xs text-white"
                    onClick={() => onOpenProductOffline()}
                  >
                    <div className="flex gap-2 text-white">
                      <Save size={18} />
                      <div
                        className={`px-2 rounded-full text-xs font-medium bg-orange-300`}
                      >
                        {data.ProductsFromCustomer.length}
                      </div>
                    </div>
                    <TypographyMuted className="text-white">
                      Salvar{' '}
                    </TypographyMuted>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </nav>
      <nav className="hidden md:block sticky">
        <div className="flex gap-4 items-center h-16">
          <div className="flex justify-center">
            <Button
              className="flex items-center gap-2 text-xs bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() =>
                navigate(
                  `/market-research/packaging-research?search=${marketResearch.SK_PESQUISA}`,
                )
              }
            >
              <Package size={16} />
              <TypographyMuted className="text-white">
                Digitação por Embalagem
              </TypographyMuted>
            </Button>
          </div>
          <div className="flex justify-center">
            <Button
              className="flex items-center gap-2 text-xs"
              onClick={() => onOpenAddProduct()}
            >
              <TypographyMuted className="text-white">Informar</TypographyMuted>
            </Button>
          </div>

          <div className="flex justify-center">
            <Button
              className="flex items-center gap-2 text-xs"
              onClick={() => onOpenAddNewProduct()}
            >
              <TypographyMuted className="text-white">Novo</TypographyMuted>
            </Button>
          </div>
          <div className="flex justify-center">
            {data.ProductsFromCustomer?.length > 0 && (
              <Button
                className="flex items-center text-xs gap-2"
                onClick={() => onOpenProductOffline()}
              >
                <TypographyMuted className="text-white">
                  Salvar{' '}
                </TypographyMuted>
                <div className="flex gap-2 ">
                  <div className={`text-xs font-medium text-orange-500`}>
                    ( {data.ProductsFromCustomer.length} )
                  </div>
                </div>
              </Button>
            )}
          </div>
        </div>
      </nav>

      {/* Header com busca e contagem geral */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 mt-2">
        <div className="flex items-center gap-2">
          <Store className="w-5 h-5 text-primary" />
          <div>
            <TypographyH3 className="text-base font-bold text-gray-900 leading-tight">
              Produtos Coletados por Local de Pesquisa
            </TypographyH3>
            <TypographyMuted className="text-xs text-gray-500">
              {groupedByClient.length}{' '}
              {groupedByClient.length === 1 ? 'local' : 'locais'} (
              {totalProducts}{' '}
              {totalProducts === 1 ? 'produto coletado' : 'produtos coletados'})
            </TypographyMuted>
          </div>
        </div>

        {totalProducts > 0 && (
          <div className="relative w-full sm:w-72">
            <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Filtrar por cliente ou produto..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="pl-9 text-xs h-9 bg-white rounded-xl border-gray-200"
            />
          </div>
        )}
      </div>

      {groupedByClient.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-8 bg-white rounded-2xl border border-gray-200 text-center my-4 shadow-sm">
          <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center text-gray-400 mb-3">
            <Inbox size={24} />
          </div>
          <p className="text-sm font-semibold text-gray-700">
            {searchFilter
              ? 'Nenhum resultado encontrado para a busca'
              : 'Nenhum produto coletado nesta pesquisa até o momento'}
          </p>
          <p className="text-xs text-gray-400 mt-1 max-w-sm">
            {searchFilter
              ? 'Tente alterar os termos do filtro para localizar os produtos.'
              : 'Utilize os botões "Informar" ou "Digitação por Embalagem" acima para registrar coletas de preços.'}
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {groupedByClient.map((clientGroup) => {
            const isExpanded = Boolean(
              expandedClients[clientGroup.skCliente],
            );
            const publishedCount = clientGroup.products.filter(
              (p) => p.DD_STATUS === 'Publicado',
            ).length;
            const draftCount = clientGroup.products.length - publishedCount;

            return (
              <div
                key={clientGroup.skCliente}
                className="bg-white rounded-2xl border border-gray-200/90 shadow-sm overflow-hidden transition-all duration-200"
              >
                {/* Header do Cliente */}
                <div
                  onClick={() => toggleExpandClient(clientGroup.skCliente)}
                  className="flex items-center justify-between p-3.5 sm:p-4 bg-gray-50/70 hover:bg-gray-100/60 cursor-pointer border-b border-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <Store size={18} />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-sm text-gray-900 truncate">
                          {clientGroup.nomeFantasia}
                        </span>

                        {clientGroup.dsRevenda && (
                          <Badge
                            variant="outline"
                            className="text-[10px] px-1.5 py-0 bg-primary/5 border-primary/20 text-primary font-medium shrink-0"
                          >
                            {clientGroup.dsRevenda}
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 mt-0.5">
                        {clientGroup.products.length}{' '}
                        {clientGroup.products.length === 1
                          ? 'produto coletado'
                          : 'produtos coletados'}
                        {publishedCount > 0 &&
                          ` • ${publishedCount} publicado(s)`}
                        {draftCount > 0 && ` • ${draftCount} pendente(s)`}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-600 p-1"
                    >
                      {isExpanded ? (
                        <ChevronUp size={18} />
                      ) : (
                        <ChevronDown size={18} />
                      )}
                    </button>
                  </div>
                </div>

                {/* Grid de Produtos do Cliente */}
                {isExpanded && (
                  <div className="p-3 sm:p-4 bg-gray-50/30">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {clientGroup.products.map((item, pIndex) => (
                        <CardsResearchedProducts
                          key={item.SK_PRODUTOS_PESQUISADOS || pIndex}
                          dataResearchedProducts={item}
                          marketResearch={marketResearch}
                          handleViewImage={handleViewImage}
                          handleEditProduct={handleEditProduct}
                          handleDeleteProduct={handleDeleteProduct}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {isFetching && (
        <div className="absolute flex top-0 left-0 items-center justify-center w-screen h-screen z-50 bg-[#ffffff82]">
          <Lottie animationData={Money} className="md:w-1/4" />
        </div>
      )}
      {selectProduct && (
        <EditProductMarketResearchDialog
          isOpen={isOpenEditProduct}
          onClose={onCloseEditProduct}
          itemEdit={selectProduct}
          handleViewImage={handleViewImage}
        />
      )}

      {selectProduct && (
        <DeleteProductMarketResearchDiaload
          dataResearchedProducts={selectProduct}
          isOpen={isOpenDeleteProduct}
          onClose={onCloseDeleteProduct}
        />
      )}

      <AddProductMarketResearchDialog
        isOpen={isOpenAddProduct}
        onClose={onCloseAddProduct}
        MarketResearch={marketResearch}
      />

      <AddNewProductInProductMarketResearchDialog
        isOpen={isOpenAddNewProduct}
        onClose={onCloseAddNewProduct}
        MarketResearch={marketResearch}
      />

      <ProductsOfflineDialog
        isOpen={isOpenProductOffline}
        onClose={onCloseProductOffline}
      />

      {srcImage && (
        <ViewImageDialog
          isOpen={isOpenViewImage}
          onClose={onCloseViewImage}
          src={srcImage}
        />
      )}
    </div>
  );
};

export default AgentLayout;
